import * as d3 from 'd3';
import _ from 'lodash';

import {topoUtils} from './topo-utils';

const stateCentroidOffsets = {
  "12": [1.024, 1.02], //FL
  "26": [1.022, 1.18], //MI
  "15": [1.05, 1.03],  //HI
  "34": [.999, .95],   //NJ
  "24": [.995, .99],   //MD
  "21": [1, 1.02],     //KY
  "50": [.999, .97],   //VT
  "33": [1, 1.08],     //NH
  "22": [0.98, 1],     //LA
  "16": [1, 1.1],      //ID
  "6": [0.9, 1.01],    //CA
  "25": [1, 1.02],     //MA
  "9": [1, 1.03],      //CT
  "47": [1, 1.02]      //TN
};

class GeoUtils {

  constructor() {
    this.cache = {};
  }

  /**
   * Create a geoAlbersUsa projection and geo path
   * with the map centered and scaled to fit.
   */
  getAlbersUsaPath(width, height) {
    const projection = d3.geoAlbersUsa()
      .translate([width / 1.98, height / 2.04])
      .scale(Math.min(width * 1.3, height * 2.17));

    return d3.geoPath().projection(projection);
  }

  getAlbersStatePathProjection(stateFips, width, height) {
    const topo = topoUtils.getStateTopography(stateFips);
    const projection = stateFips === 2 ? d3.geoAlbersUsa() : d3.geoMercator();
    const path = d3.geoPath().projection(projection);

    projection.scale(1).translate([0, 0]);

    const b = path.bounds(topo.land),
      s = .98 / Math.max((b[1][0] - b[0][0]) / width, (b[1][1] - b[0][1]) / height),
      t = [(width - s * (b[1][0] + b[0][0])) / 2, (height - s * (b[1][1] + b[0][1])) / 2];

    projection.scale(s).translate(t);

    return {
      path,
      projection
    };
  }

  getAlbersStatePath(stateFips, width, height) {
    return this.getAlbersStatePathProjection(stateFips, width, height).path;
  }

  getAlbersStateProjection(stateFips, width, height) {
    return this.getAlbersStatePathProjection(stateFips, width, height).projection;
  }

  offsetStateCentroid(stateFips, point) {
    const offset = stateCentroidOffsets[stateFips];

    if (offset) {
      return [
        point[0] * offset[0],
        point[1] * offset[1]
      ]
    }

    return point;
  };

  getMercatorStateCountyPaths(stateFips, width, height) {
    const key = `mercator-state-${stateFips}- ${Math.round(width)}-${Math.round(height)}`;

    if (!this.cache[key]) {
      const topo = topoUtils.getStateTopography(stateFips);
      const path = this.getAlbersStatePath(stateFips, width, height);
      this.cache[key] = _.map(topo.counties, d => {
        return {
          id: d.id,
          countyFips: d.id,
          stateFips: Math.floor(d.id / 1000),
          path: path(d),
          cent: path.centroid(d)
        }
      });
    }

    return this.cache[key] || {};
  }

  getAlbersUsaCountyPaths(width, height) {
    const key = `albers-county-${Math.round(width)}-${Math.round(height)}`;

    if (!this.cache[key]) {
      const topo = topoUtils.getNationalTopography();
      const path = this.getAlbersUsaPath(width, height);
      this.cache[key] = _.map(topo.counties, d => {
        return {
          id: d.id,
          countyFips: d.id,
          stateFips: Math.floor(d.id / 1000),
          path: path(d),
          cent: path.centroid(d)
        }
      });
    }

    return this.cache[key] || {};
  }

  getAlbersUsaStatePaths(width, height) {
    const key = `albers-state-${Math.round(width)}-${Math.round(height)}`;

    if (!this.cache[key]) {
      const topo = topoUtils.getNationalTopography();
      const path = this.getAlbersUsaPath(width, height);
      this.cache[key] = _.map(topo.states, d => {
        return {
          id: d.id,
          stateFips: Math.floor(d.id / 1000),
          path: path(d),
          cent: this.offsetStateCentroid(d.id, path.centroid(d))
        }
      });
    }

    return this.cache[key] || {};
  }

  createMultiScale(data, dimX, dimY, year, type) {

    const blueColors = ['#1b1b1b', '#1b1c34', '#253261', '#2d478a', '#3660b9'];
    const redColors = ['#1b1b1b', '#33101b', '#621425', '#8a192f', '#b51e38'];

    const bivarColors = [
      ['#1b1b1b', '#33101b', '#561523', '#7a1a2c', '#a01f35'],
      ['#1b1c34', '#3d1c3d', '#621d48', '#881c53', '#b01c5d'],
      ['#232e57', '#482962', '#6e246e', '#961e7b', '#c01787'],
      ['#2c417b', '#523789', '#7b2c97', '#a520a5', '#d013b3'],
      ['#3554a2', '#5d45b2', '#8733c1', '#b321d1', '#e00de0']
    ];

    const yearIndex = 2015 - (year || 2015);
    const dataIsArray = _.isArray(data);

    const valueX = dataIsArray ?
      row => _.get(row, [dimX, yearIndex], 0) :
      row => _.get(data, [row.id, dimX, yearIndex], 0);

    const valueY = dataIsArray ?
      row => _.get(row, [dimY, yearIndex], 0) :
      row => _.get(data, [row.id, dimY, yearIndex], 0);

    // Filter out zero values since they are redacted locations in most cases with this dataset
    const values = _.chain(data)
      .map(d => {
        return {
          x: _.get(d, [dimX, yearIndex], 0),
          y: _.get(d, [dimY, yearIndex], 0)
        }
      })
      .filter(d => d.x > 0 && d.y > 0)
      .value();

    const valuesX = _.map(values, 'x');
    const valuesY = _.map(values, 'y');

    const extentX = d3.extent(valuesX);
    const extentY = d3.extent(valuesY);

    const scaleX = d3.scaleQuantile()
      .domain(valuesX)
      .range([0, 1, 2, 3, 4]);

    const scaleY = d3.scaleQuantile()
      .domain(valuesY)
      .range([0, 1, 2, 3, 4]);

    let outputScale = null;
    let outputColors = null;

    if (type === 'x') {
      outputScale = function (row) {
        const iX = valueX ? scaleX(valueX(row)) : 0;
        return _.get(redColors, iX, redColors[0]);
      };

      outputColors = _.map(redColors, (c, x) => {
        return {c, y: 0, x}
      });
    }
    else if (type === 'y') {
      outputScale = function (row) {
        const iY = valueY ? scaleY(valueY(row)) : 0;
        return _.get(blueColors, iY, blueColors[0]);
      };

      outputColors = _.map(blueColors, (c, x) => {
        return {c, y: 0, x}
      });
    }
    else {
      outputScale = function (row) {
        const iX = valueX ? scaleX(valueX(row)) : 0;
        const iY = valueY ? scaleY(valueY(row)) : 0;

        return _.get(bivarColors, [iY, iX], bivarColors[0][0]);
      };

      outputColors = _.reduce(bivarColors, (reducer, row, y) => {
        _.forEach(row, (c, x) => reducer.push({c, y, x}));
        return reducer;
      }, []);
    }

    outputScale.scaleX = scaleX;
    outputScale.scaleY = scaleY;
    outputScale.valueX = valueX;
    outputScale.valueY = valueY;
    outputScale.valuesX = () => _.cloneDeep(valuesX);
    outputScale.valuesY = () => _.cloneDeep(valuesY);
    outputScale.extentX = () => _.cloneDeep(extentX);
    outputScale.extentY = () => _.cloneDeep(extentY);
    outputScale.quantilesX = () => _.cloneDeep(scaleX.quantiles());
    outputScale.quantilesY = () => _.cloneDeep(scaleY.quantiles());
    outputScale.colors = () => _.cloneDeep(outputColors);


    return outputScale;
  }

}

export let geoUtils = new GeoUtils();


// WEBPACK FOOTER //
// ./src/charts/utils/geo-utils.js